import React, { useState } from 'react';
/**
 * `Product` renders a selectable product card.
 *
 * Features:
 * - Shows selection state (tick) and "Add to plan" / "Remove from plan"
 *   actions via `ButtonAdd` and `ButtonRemove`.
 * - Optionally displays quoted variant pricing via `QuotedVariantPrice`.
 * - Can display key benefits synopsis in the footer and/or render benefits
 *   directly in-card.
 * - Supports a "Show more" modal (`ShowBenefitsModal`) when key benefits are
 *   enabled.
 * - Supports a GAP cool-off warning state that disables selection.
 */

import './Product.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-regular-svg-icons/faCheckCircle';
import { ButtonAdd } from '../Button/ButtonAdd';
import { ButtonRemove } from '../Button/ButtonRemove';
import QuotedVariantPrice from '../../sitePages/QuotedVariantPrice';
import { useTranslation } from 'react-i18next';
import Callout from '../../sitePages/Callout';
import ShowBenefitsModal from '../ShowBenefitsModal';
import { KeyBenefitList } from '../CommonTypes';
import { renderKeyBenefits } from '../ProductMethods';

export type ProductProps = {
  id: string;
  title: string;
  isSelected: boolean;
  showMoreItems?: KeyBenefitList[];
  synopsisText?: string;
  showQuotedPrice?: boolean;
  monthlyPrice?: number;
  annualPrice?: number;
  paymentMethod?: string;
  noOfInstallments?: number;
  isPurchased: boolean;
  isDisabled: boolean;
  addOrRemoveProduct: (id: string) => void;
  showCooloffWarning: boolean;
  cooloffWarningText?: string;
  culture: string;
  showKeyBenefits: boolean;
  hideRemovePlanLink?: boolean;
  showKeyBenefitsInProductCard?: boolean;
};

const Product = ({
  id,
  title,
  isSelected,
  showMoreItems,
  synopsisText,
  showQuotedPrice,
  monthlyPrice,
  annualPrice,
  paymentMethod,
  noOfInstallments,
  isPurchased,
  isDisabled,
  addOrRemoveProduct,
  showCooloffWarning,
  cooloffWarningText,
  culture,
  showKeyBenefits,
  hideRemovePlanLink,
  showKeyBenefitsInProductCard
}: ProductProps): JSX.Element => {
  const [showModal, setShowModal] = useState<boolean>(false);

  const onShowMore = () => {
    setShowModal(true);
  };

  const onCloseModal = () => {
    setShowModal(false);
  };

  const addOrRemovePlan = () => {
    addOrRemoveProduct(id);
  };

  const { t } = useTranslation();

  const renderShowMoreItems = () => {
    return renderKeyBenefits(showMoreItems);
  };

  const disableProduct = isPurchased || showCooloffWarning;
  console.log('showKeyBenefitsInProductCard', showKeyBenefitsInProductCard);
  return (
    <div
      className={`osp-card${
        isSelected && !disableProduct ? ' selected' : ''
      } product-card cms-card${disableProduct ? ' product-card-disabled' : ''}`}>
      <div className='product-card-header'>
        <h4>
          {title}
          {isSelected && !showCooloffWarning && (
            <FontAwesomeIcon icon={faCheckCircle} className='tick' />
          )}
        </h4>
      </div>
      {showQuotedPrice && !isPurchased && (
        <QuotedVariantPrice
          noOfInstallments={noOfInstallments}
          totalPrice={annualPrice}
          monthlyPrice={monthlyPrice}
          paymentMethod={paymentMethod}
          culture={culture}
        />
      )}
      <div className='product-card-body'>
        {showCooloffWarning && (
          <div className='m2-top'>
            <Callout text={cooloffWarningText ?? ''} isWarning={true} />
          </div>
        )}
        {!showCooloffWarning && (
          <div className='product-card-button-container'>
            {isSelected && !isPurchased && !hideRemovePlanLink && (
              <ButtonRemove
                name='btn-remove-plan-${id}'
                onClick={addOrRemovePlan}
                text={t('customer-product-page.remove-from-plan')}
              />
            )}
            {!isSelected && !isPurchased && (
              <ButtonAdd
                name='btn-add-plan-${id}'
                onClick={addOrRemovePlan}
                disabled={isDisabled}
                text={t('customer-product-page.add-to-plan')}
              />
            )}
            {isPurchased && <p className='p2'>Purchased</p>}
          </div>
        )}
      </div>
      {showKeyBenefits && (
        <div className='product-footer'>
          <p className='cms'>{synopsisText}</p>
        </div>
      )}
      {showKeyBenefitsInProductCard && (
        <div className='product-footer'>
          <br></br>
          <p className='cms'>{renderShowMoreItems()}</p>
        </div>
      )}
      {showKeyBenefits &&
        (showKeyBenefitsInProductCard === false ||
          showKeyBenefitsInProductCard === undefined) && (
          <div>
            <ButtonRemove
              name='btn-show-more'
              text={t('customer-product-page.show-more')}
              additionalClassNames='small'
              onClick={onShowMore}
            />
            <ShowBenefitsModal
              showModal={showModal}
              title={title}
              showMoreItems={showMoreItems}
              onCloseModal={onCloseModal}
            />
          </div>
        )}
    </div>
  );
};

export default Product;
